/**
 * web/src/hooks/queries/useInviteThreads.ts
 * Kickoff thread-picker (design §5): lists recent Gmail threads (last year) the
 * reviewer already has with a speaker email, so the invite can be sent as a
 * REPLY on an existing thread instead of a fresh one. Reads the CALLER's own
 * connected mailboxes server-side (read-only); a mailbox that can't be read is
 * skipped, so the list degrades to whatever it can show. Disabled until the
 * picker is expanded AND an email exists, keeping the (per-mailbox N+1) Gmail
 * read off the dialog's happy path. Cache key
 * ['speakers','invite-threads',eventId,email]; 5-minute staleTime avoids
 * re-searching Gmail every time the picker re-expands.
 */
import { useQuery } from '@tanstack/react-query';
import { fetchInviteThreads, type InviteThreadCandidate } from '../../api/speakers';
import { qk } from '../../lib/queryKeys';

/**
 * Fetches candidate invite-reply threads for a speaker email.
 *
 * @param eventId the speaker's event (access-checked server-side)
 * @param email the speaker's email (empty disables the query)
 * @param enabled gate on picker-expanded (avoid Gmail reads while collapsed)
 * @returns React Query result for InviteThreadCandidate[]
 * @sideEffects HTTP GET (read-only Gmail API server-side)
 * @idempotent yes
 */
export function useInviteThreads(
  eventId: string | undefined,
  email: string | null | undefined,
  enabled: boolean,
) {
  const trimmed = (email ?? '').trim();
  return useQuery<InviteThreadCandidate[]>({
    queryKey: qk.speakers.inviteThreads(eventId ?? '', trimmed),
    queryFn: () => fetchInviteThreads(eventId as string, trimmed),
    enabled: enabled && Boolean(eventId) && trimmed.length >= 3,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}
